import React from 'react';
import { 
  FileText, ShieldCheck, Printer, 
  Share2, Lock, CheckCircle, ArrowRight,
  HardDrive, PenTool,
  CreditCard
} from 'lucide-react';

const ReportShowcase = () => {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center py-20 border-t border-slate-800/50">
      
      {/* --- LEFT SIDE: THE PITCH --- */}
      <div className="space-y-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-widest">
          <CheckCircle size={12} />
          ISO 17640 Compliant Output
        </div>
        
        <h2 className="text-5xl font-black uppercase tracking-tighter leading-[0.95]">
          From Raw Data to <br />
          <span className="text-indigo-500">Certified PDF.</span>
        </h2>
        
        <div className="space-y-6">
          <p className="text-slate-400 text-lg font-medium leading-relaxed">
            The final stage of the EvalNDT pipeline is the synthesis of a comprehensive 
            technical report. Our engine doesn't just list flaws; it compiles a 
            professional engineering document including:
          </p>
          
          <ul className="space-y-4">
            {[
              { title: "Automatic Sizing", desc: "Length, height, and depth calculated per standard." },
              { title: "Weld Geometry", desc: "Visual mapping of the root, cap, and heat-affected zones." },
              { title: "Equipment Logs", desc: "Automatic pull of Probe, Wedge, and Machine serials." }
            ].map((item, i) => (
              <li key={i} className="flex gap-4 group">
                <div className="mt-1 text-indigo-500 group-hover:translate-x-1 transition-transform">
                  <ArrowRight size={18} />
                </div>
                <div>
                  <h4 className="text-sm font-black uppercase text-slate-200">{item.title}</h4>
                  <p className="text-xs text-slate-500 font-medium">{item.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

       <div className="pt-6 flex flex-wrap gap-4">
    <button className="px-10 py-5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-black uppercase text-[11px] tracking-[0.2em] transition-all shadow-lg shadow-indigo-900/40 flex items-center gap-3 group">
      Start Free Trial 
      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
    </button>
    <button className="px-10 py-5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-2xl font-black uppercase text-[11px] tracking-[0.2em] transition-all flex items-center gap-3 border border-slate-700">
      <CreditCard size={16} /> Subscription Plans
    </button>
  </div>
      </div>

      {/* --- RIGHT SIDE: THE A4 TEMPLATE --- */}
      <div className="relative group">
        {/* Background "Shadow" Glow */}
        <div className="absolute -inset-4 bg-indigo-500/10 rounded-[3rem] blur-3xl opacity-50 group-hover:opacity-100 transition-opacity" />
        
        {/* The Paper Container */}
        <div className="relative bg-white aspect-[1/1.414] w-full max-w-[550px] mx-auto rounded-sm shadow-2xl overflow-hidden border border-slate-200 transform group-hover:-rotate-1 transition-transform duration-700">
          
          {/* CONTENT: BLURRED BUT VISIBLE STRUCTURE */}
          <div className="p-8 space-y-6 blur-[3px] opacity-40 select-none pointer-events-none">
            {/* Report Header */}
            <div className="flex justify-between items-start border-b-4 border-slate-900 pb-6">
              <div className="space-y-1">
                <p className="text-[8px] font-black uppercase text-slate-400">Organization</p>
                <h3 className="text-xl font-black text-slate-900">ABC INSPECTION SERVICES</h3>
                <p className="text-[7px] font-bold text-slate-500 uppercase tracking-tighter leading-none">
                  Advanced NDT Division • Industrial Quality Control
                </p>
              </div>
              <div className="text-right">
                <div className="w-16 h-16 bg-slate-900 ml-auto flex items-center justify-center text-white text-[8px] font-black">LOGO</div>
              </div>
            </div>

            {/* Project Specs Grid */}
            <div className="grid grid-cols-3 gap-2 border-b border-slate-100 pb-4">
              {[
                { label: "Report No", val: "FNT-2026-001" },
                { label: "Date", val: "24/APR/2026" },
                { label: "Standard", val: "ASME SEC V" },
                { label: "Client", val: "Global Energy Corp" },
                { label: "Location", val: "Site-A, Sector 4" },
                { label: "Weld ID", val: "PL-092-A" }
              ].map((spec, i) => (
                <div key={i} className="space-y-0.5">
                  <p className="text-[6px] font-black text-slate-400 uppercase">{spec.label}</p>
                  <p className="text-[8px] font-bold text-slate-800">{spec.val}</p>
                </div>
              ))}
            </div>

            {/* Indication Table Preview */}
            <div className="space-y-2">
               <div className="w-full h-4 bg-slate-100" />
               <div className="w-full h-24 bg-slate-50 border border-slate-100 flex items-center justify-center italic text-[10px] text-slate-300">
                 [Sectorial Scan Image Injected Here]
               </div>
               <div className="space-y-1">
                 {[...Array(4)].map((_, i) => (
                   <div key={i} className="w-full h-2 bg-slate-50" />
                 ))}
               </div>
            </div>

            {/* Signatures */}
            <div className="pt-10 flex justify-between">
               <div className="w-24 border-t border-slate-300 pt-1 text-[6px] font-black text-slate-400">Inspector (Level II)</div>
               <div className="w-24 border-t border-slate-900 pt-1 text-[6px] font-black text-slate-900 italic font-serif">Verified By EvalNDT Logic</div>
               <div className="w-24 border-t border-slate-300 pt-1 text-[6px] font-black text-slate-400">Level III Approval</div>
            </div>
          </div>

          {/* OVERLAY: THE LOCK UI */}
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-indigo-900/10 backdrop-blur-[1px]">
            <div className="bg-white/90 backdrop-blur-md p-8 rounded-3xl shadow-xl border border-white text-center max-w-[80%] transform scale-90 group-hover:scale-100 transition-transform">
              <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-200">
                <Lock className="text-white" size={24} />
              </div>
              <h4 className="text-lg font-black text-slate-900 uppercase tracking-tight">Full Report Layout</h4>
              <p className="text-[10px] text-slate-500 font-bold leading-tight mt-2 uppercase">
                Initialize the logic engine to generate and export your professional ABC Inspection report.
              </p>
              <div className="mt-6 flex flex-col gap-2">
                <button className="w-full py-3 bg-slate-900 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-colors">
                  Generate My Draft
                </button>
              </div>
            </div>
          </div>

          {/* Floating Indicators */}
          <div className="absolute top-4 right-4 flex gap-2">
             <div className="px-2 py-1 bg-white/80 border border-slate-200 rounded-md shadow-sm text-[8px] font-black text-indigo-600">A4_ISO</div>
             <div className="px-2 py-1 bg-white/80 border border-slate-200 rounded-md shadow-sm text-[8px] font-black text-indigo-600 flex items-center gap-1">
               <Printer size={10} /> PDF
             </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReportShowcase;